// MESSAGE SOURCES (busy-timeline-srv#29) — SMS, WhatsApp-style messages and phone calls, entering
// Busy through busy-source-srv and arriving on the same object model as mail: a message THREAD is
// a linked thread of an Ongoing (`kind: 'message'` in the story's `emails` list), and a call the
// user writes down is a message of source `call`.
//
// This file is the pure half of two surfaces:
//
//   • the "Log a call" entry in the universal input — the form's shape, its validation, and the
//     payload `POST v1/sources/calls` takes (who, when, how long, notes);
//   • how a message thread READS on the rails — its module (glyph + colour) per source, and the
//     "N messages" the story Briefing counts.
//
// On the vocabulary: the web's module palette already uses the word `message` for an EMAIL (the
// Global timeline panel's own type string, `globalTimelineFeed.js`), and that alias is load-bearing
// across the panel and its tests. So the MODULE these threads belong to is called `messaging` in
// `moduleIcons`/`moduleColors`, and this file is where a server-side `message` kind, source or
// origin is translated to it — nothing else has to know the two words differ.

import { moduleColor } from '@/lib/moduleColors'
import { moduleIcon } from '@/lib/moduleIcons'

/** The server's source slugs. */
export const SOURCES = ['sms', 'whatsapp', 'call', 'other']

/** The universal input's mode for the call log — sits beside 'tasks' | 'notes' | 'events' | 'emails' | 'contacts'. */
export const CALL_MENU = 'calls'

/** The `+` menu entry: the phone glyph in the messaging module's colour. */
export function callLogOption() {
  return { menu: CALL_MENU, icon: 'phone', label: 'Log a call', color: moduleColor('messaging') }
}

/**
 * The module a message thread (or a message-origin story) is drawn in. A call gets the phone
 * glyph, every other source the message bubble; the colour is the messaging module's either way.
 *
 * @param source 'sms' | 'whatsapp' | 'call' | 'other' | undefined
 */
export function messageMeta(source) {
  const s = String(source || '').trim().toLowerCase()
  return {
    icon: s === 'call' ? 'phone' : moduleIcon('messaging'),
    color: moduleColor('messaging'),
    label: s === 'call' ? 'Call' : 'Message',
  }
}

// ── the call log form ──────────────────────────────────────────────────────────────────────────

/** The form as the box holds it: who (free text — a name or a number), when, duration in minutes, notes. */
export const EMPTY_CALL = { who: '', when: '', minutes: '', notes: '' }

/**
 * What is wrong with the form, or '' when it can be saved. Only WHO is required: a call is a fact
 * about a person; when defaults to now, and a call without notes or a duration is still a call.
 */
export function validateCallLog(form) {
  const who = String(form?.who || '').trim()
  if (!who) return 'Say who the call was with'
  const minutes = String(form?.minutes ?? '').trim()
  if (minutes && !(Number(minutes) >= 0)) return 'Duration must be a number of minutes'
  if (form?.when && Number.isNaN(new Date(form.when).getTime())) return 'That date could not be read'
  return ''
}

/**
 * "Ada Lovelace" → a named participant with no address; "+90 532 123 4567" → an address with no name;
 * "Ada <+905321234567>" or "Ada, +905321234567" → both. The address is whatever looks like a phone
 * number or an email; the service normalises it (E.164, lowercase) and matches it to a contact.
 */
export function parseWho(who) {
  const text = String(who || '').trim()
  if (!text) return []
  const angle = text.match(/^(.*?)\s*<([^>]+)>\s*$/)
  if (angle) return [{ name: angle[1].trim() || null, address: angle[2].trim() }]
  const parts = text.split(/\s*[,;]\s*/).filter(Boolean)
  const address = parts.find(looksLikeAddress)
  const name = parts.filter(p => p !== address).join(' ').trim()
  if (address) return [{ name: name || null, address }]
  return [{ name: text, address: '' }]
}

/**
 * A phone number (5+ digits, only number punctuation) or an email.
 *
 * The opening bracket is in the FIRST position too: "(0532) 123 4567" is how a number is written in
 * half the world, and without it the line read as a NAME — the call was then logged against a
 * participant with no address at all, which busy-source-srv can never merge onto the contact.
 */
export function looksLikeAddress(text) {
  const s = String(text || '').trim()
  if (!s) return false
  if (s.includes('@')) return /^[^\s@]+@[^\s@]+$/.test(s)
  return /^[+\d(][\d\s\-().]*$/.test(s) && (s.match(/\d/g) || []).length >= 5
}

/**
 * The request body for `POST v1/sources/calls`. Minutes become seconds, a blank "when" is left out
 * (the service stamps now), and a participant without an address still travels with its name so
 * the thread is titled after the person even before a number is known.
 */
export function callLogPayload(form, { direction = 'out' } = {}) {
  const participants = parseWho(form?.who).map(p => ({
    address: p.address || null,
    name: p.name || null,
  }))
  const minutes = String(form?.minutes ?? '').trim()
  const payload = {
    participants,
    direction,
    notes: String(form?.notes || '').trim() || null,
    durationSeconds: minutes ? Math.round(Number(minutes) * 60) : null,
  }
  if (form?.when) {
    const when = new Date(form.when)
    if (!Number.isNaN(when.getTime())) payload.occurredUtc = when.toISOString()
  }
  return payload
}

/**
 * Post the call. `api` is the app's general service (`insert(service, model)`); the answer is the
 * ingest receipt `{ messageId, timelineId, created, isNewThread }`, or null when nothing came back.
 */
export async function logCall(api, form, opts) {
  const res = await api.insert('sources/calls', callLogPayload(form, opts))
  return res?.data?.data ?? res?.data ?? null
}

/** The story-step line a logged call writes: "Call logged: Ada Lovelace" and its detail. */
export function callStepLabel(form) {
  const who = parseWho(form?.who)[0]
  const name = who?.name || who?.address || 'someone'
  return `Call logged: ${name}`
}

export function callStepDetail(form) {
  const bits = []
  const minutes = String(form?.minutes ?? '').trim()
  if (minutes && Number(minutes) > 0) bits.push(`${Number(minutes)} min`)
  const notes = String(form?.notes || '').trim()
  if (notes) bits.push(notes)
  return bits.join(' · ')
}

// ── message threads on the rails ───────────────────────────────────────────────────────────────

/** The story's linked threads that are MESSAGE conversations (`kind: 'message'`). */
export function messageThreads(story) {
  return (story?.emails || []).filter(t => t && String(t.kind || '').toLowerCase() === 'message')
}

/** How many message threads the story has — the Briefing's "N messages" row. */
export function messageThreadCount(story) {
  return messageThreads(story).length
}

/**
 * The source of a message thread, read off its subject: busy-source-srv titles a call thread
 * "Call · <who>", and every other source is a message. Cheap and honest — the story payload does
 * not carry the source, and the glyph is the only thing that needs it.
 */
export function threadSource(thread) {
  return /^call\b/i.test(String(thread?.subject || '')) ? 'call' : 'sms'
}
